import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import {
  EventType,
  LogSeverity,
  TelemetryData,
  ITelemetryService,
} from './TelemetryService.types';

// Níveis de log
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// Tipo para os metadados de telemetria
export interface TelemetryMetadata {
  sessionId: string;
  correlationId?: string;
  userId?: string;
  timestamp: string;
  level: LogLevel;
  source: string;
  eventType: EventType;
  data?: Record<string, any>;
}

/**
 * Serviço de telemetria para registro de eventos e métricas
 */
class TelemetryService implements ITelemetryService {
  private static instance: TelemetryService;
  private sessionId: string;
  private correlationIds: Map<string, string> = new Map();
  private isEnabled: boolean = true;
  private logLevel: LogLevel = 'info';
  private shouldConsoleLog: boolean = process.env.EXPO_PUBLIC_ENV === 'DEV';
  private baseUrl: string = '';
  private queue: any[] = [];
  private isProcessingQueue: boolean = false;
  private localStorageKey = 'telemetry_queue';

  private constructor() {
    this.sessionId = uuidv4();
    this.loadQueueFromStorage();

    // Processar a fila quando a conexão for restaurada
    window.addEventListener('online', () => {
      this.processQueue();
    });
  }

  /**
   * Obtém a instância singleton do serviço
   */
  public static getInstance(): TelemetryService {
    if (!TelemetryService.instance) {
      TelemetryService.instance = new TelemetryService();
    }
    return TelemetryService.instance;
  }

  /**
   * Configura o serviço de telemetria
   */
  public configure(options: {
    enabled?: boolean;
    logLevel?: LogLevel;
    shouldConsoleLog?: boolean;
    baseUrl?: string;
  }): void {
    if (options.enabled !== undefined) this.isEnabled = options.enabled;
    if (options.logLevel) this.logLevel = options.logLevel;
    if (options.shouldConsoleLog !== undefined)
      this.shouldConsoleLog = options.shouldConsoleLog;
    if (options.baseUrl) this.baseUrl = options.baseUrl;

    // Iniciar processamento da fila se tiver URL base
    if (this.baseUrl && navigator.onLine) {
      this.processQueue();
    }

    console.info('[TelemetryService] Configurado com URL base:', this.baseUrl);
  }

  /**
   * Cria um novo ID de correlação para rastrear eventos relacionados
   */
  public createCorrelationId(prefix: string = 'telemetry'): string {
    return `${prefix}_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 9)}`;
  }

  /**
   * Obtém um ID de correlação existente por contexto
   */
  public getCorrelationId(context: string): string | undefined {
    return this.correlationIds.get(context);
  }

  /**
   * Registra um evento de telemetria
   */
  public logEvent(
    source: string,
    eventType: EventType,
    level: LogLevel = 'info',
    data?: Record<string, any>,
    correlationId?: string
  ): void {
    if (!this.isEnabled || !this.shouldLogLevel(level)) return;

    const metadata: TelemetryMetadata = {
      sessionId: this.sessionId,
      correlationId,
      timestamp: new Date().toISOString(),
      level,
      source,
      eventType,
      data,
    };

    // No ambiente de desenvolvimento, exibe no console
    if (this.shouldConsoleLog) {
      const logMethod = this.getConsoleMethod(level);
      logMethod(`[${level.toUpperCase()}] ${source} - ${eventType}`, metadata);
    }

    // Envia para o backend se tiver URL configurada
    this.sendToBackend(metadata);
  }

  /**
   * Verifica se deve registrar o nível especificado
   */
  private shouldLogLevel(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = levels.indexOf(this.logLevel);
    const logLevelIndex = levels.indexOf(level);
    return logLevelIndex >= currentLevelIndex;
  }

  /**
   * Obtém o método de console adequado para o nível
   */
  private getConsoleMethod(level: LogLevel): (...data: any[]) => void {
    switch (level) {
      case 'debug':
        return console.debug;
      case 'info':
        return console.info;
      case 'warn':
        return console.warn;
      case 'error':
        return console.error;
      default:
        return console.log;
    }
  }

  /**
   * Envia log para o backend
   */
  private async sendToBackend(metadata: TelemetryMetadata): Promise<void> {
    // Se não tiver URL base ou estiver offline, armazenar na fila
    if (!this.baseUrl || !navigator.onLine) {
      this.addToQueue(metadata);
      return;
    }

    try {
      await axios.post(`${this.baseUrl}/api/logs`, {
        level: metadata.level,
        component: metadata.source,
        eventType: metadata.eventType,
        data: metadata.data,
        correlationId: metadata.correlationId,
        timestamp: metadata.timestamp,
        userAgent: navigator.userAgent,
        source: 'web-client',
      });
    } catch (error) {
      // Em caso de erro, armazenar na fila
      this.addToQueue(metadata);
    }
  }

  /**
   * Adiciona um log à fila
   */
  private addToQueue(metadata: TelemetryMetadata): void {
    this.queue.push(metadata);

    // Salvar a fila no localStorage
    try {
      localStorage.setItem(this.localStorageKey, JSON.stringify(this.queue));
    } catch (error) {
      console.error(
        '[TelemetryService] Erro ao salvar fila no localStorage:',
        error
      );
    }
  }

  /**
   * Carrega a fila do localStorage
   */
  private loadQueueFromStorage(): void {
    try {
      const queueStr = localStorage.getItem(this.localStorageKey);
      if (queueStr) {
        this.queue = JSON.parse(queueStr);
      }
    } catch (error) {
      console.error(
        '[TelemetryService] Erro ao carregar fila do localStorage:',
        error
      );
    }
  }

  /**
   * Processa a fila de logs
   */
  private async processQueue(): Promise<void> {
    if (
      this.isProcessingQueue ||
      !navigator.onLine ||
      !this.baseUrl ||
      this.queue.length === 0
    ) {
      return;
    }

    this.isProcessingQueue = true;

    try {
      // Processar em lotes de 10
      const batch = this.queue.splice(0, 10);

      // Enviar cada item do lote
      await Promise.all(batch.map((metadata) => this.sendToBackend(metadata)));

      // Atualizar o localStorage
      localStorage.setItem(this.localStorageKey, JSON.stringify(this.queue));

      // Se ainda houver itens, continuar processando
      if (this.queue.length > 0) {
        setTimeout(() => this.processQueue(), 1000);
      }
    } catch (error) {
      console.error('[TelemetryService] Erro ao processar fila:', error);
    } finally {
      this.isProcessingQueue = false;
    }
  }

  private log(
    severity: LogSeverity,
    component: string,
    event: EventType,
    data: TelemetryData,
    correlationId?: string
  ) {
    if (!this.isEnabled) return;

    const level =
      severity === 'error' ? 'error' : severity === 'warn' ? 'warn' : 'info';

    this.logEvent(component, event, level, data, correlationId);
  }

  info(
    component: string,
    event: EventType,
    data: TelemetryData,
    correlationId?: string
  ): void {
    this.log('info', component, event, data, correlationId);
  }

  warn(
    component: string,
    event: EventType,
    data: TelemetryData,
    correlationId?: string
  ): void {
    this.log('warn', component, event, data, correlationId);
  }

  error(
    component: string,
    event: EventType,
    data: TelemetryData,
    correlationId?: string
  ): void {
    this.log('error', component, event, data, correlationId);
  }
}

export default TelemetryService.getInstance();
